import React, { useRef, useState } from 'react';
import { Controller, UseFormReturn } from 'react-hook-form';
import { Text } from 'src/modules/shared/components/atoms/text';
import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import LabelTextInput from 'src/modules/shared/components/atoms/inputs/label-text-input';
import LabelTextArea from 'src/modules/shared/components/atoms/inputs/label-text-area';
import TextArea from 'src/modules/shared/components/atoms/inputs/text-area';
import { WithTooltip } from 'src/modules/automations/components/atoms/help-tooltip/with-tooltip';
import { EmployeePersonalityCommunicationStyle } from 'src/api/generated/models/EmployeePersonalityCommunicationStyle';
import {
  EmployeeFormInputValues,
  EmployeeFormValues,
  EmployeeFormPath,
} from 'src/modules/automations/components/organisms/employees/employee-form/types';
import { ConstraintsField } from 'src/modules/automations/components/molecules/employees/form-fields/constraint-field';
import { CommunicationStyleSelector } from 'src/modules/automations/components/molecules/employees/form-fields/communication-style-selector';
import { Markdown } from 'src/modules/shared/components/molecules/markdown';
import { useSyncedScroll } from 'src/modules/shared/hooks/scroll/synced-scroll';
import { Tabs } from 'src/modules/shared/components/atoms/tabs/tabs';
import { Tab } from 'src/modules/shared/components/atoms/tabs/tab';
import { SectionSidebarMenu } from 'src/modules/shared/components/molecules/section-menu';
import { useSectionNavigation } from 'src/modules/shared/hooks/sections/section-navigation';

type SectionId = 'personality' | 'basic-info' | 'behavior' | 'knowledge-base';

const OVERVIEW_SECTIONS: { id: SectionId; label: string }[] = [
  {
    id: 'personality',
    // TODO: check when implementing i18n
    label: 'Personalidad',
  },
  {
    id: 'basic-info',
    // TODO: check when implementing i18n
    label: 'Información básica',
  },
  {
    id: 'behavior',
    // TODO: check when implementing i18n
    label: 'Comportamiento',
  },
  {
    id: 'knowledge-base',
    // TODO: check when implementing i18n
    label: 'Base de conocimiento',
  },
];

interface EmployeeOverviewTabProps {
  form: UseFormReturn<EmployeeFormInputValues, unknown, EmployeeFormValues>;
  initialObjective?: string;
}

export const EmployeeOverviewTab: React.FC<EmployeeOverviewTabProps> = ({
  form,
  initialObjective = '',
}) => {
  const {
    register,
    control,
    setValue,
    watch,
    formState: { errors },
  } = form;

  const { activeSectionId, handleSectionChange } = useSectionNavigation({
    sections: OVERVIEW_SECTIONS,
    defaultSectionId: OVERVIEW_SECTIONS[0]?.id,
  });

  const [objective, setObjective] = useState(initialObjective);
  const [newConstraint, setNewConstraint] = useState('');
  const knowledgeBaseEditRef = useRef<HTMLTextAreaElement | null>(null);
  const knowledgeBasePreviewRef = useRef<HTMLDivElement | null>(null);

  const constraints = watch(EmployeeFormPath.DEFINITION_CONSTRAINTS) || [];
  const knowledgeBase = watch(EmployeeFormPath.DEFINITION_KNOWLEDGE_BASE) || '';
  const { ref: knowledgeBaseRegisterRef, ...knowledgeBaseFieldProps } =
    register('definition.knowledgeBase');
  const hasKnowledgeBaseContent = Boolean(knowledgeBase.trim());

  useSyncedScroll({
    sourceRef: knowledgeBaseEditRef,
    targetRef: knowledgeBasePreviewRef,
    syncTrigger: knowledgeBase,
  });

  const addConstraint = (value: string) => {
    if (!value.trim()) return;

    const currentConstraints = [
      ...(watch(EmployeeFormPath.DEFINITION_CONSTRAINTS) || []),
    ];
    setValue(
      EmployeeFormPath.DEFINITION_CONSTRAINTS,
      [...currentConstraints, value.trim()],
      {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      }
    );
    setNewConstraint('');
  };

  const removeConstraint = (index: number) => {
    const currentConstraints = [
      ...(watch(EmployeeFormPath.DEFINITION_CONSTRAINTS) || []),
    ];
    setValue(
      EmployeeFormPath.DEFINITION_CONSTRAINTS,
      currentConstraints.filter((_, i) => i !== index),
      { shouldValidate: true, shouldDirty: true, shouldTouch: true }
    );
  };

  const handleObjectiveChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    setObjective(newValue);
    setValue(
      EmployeeFormPath.DEFINITION_OBJECTIVES,
      newValue ? [newValue] : [],
      {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      }
    );
  };

  return (
    <div className="mt-6 flex flex-col md:flex-row md:gap-6">
      <div className="hidden md:block md:w-60 md:flex-shrink-0">
        <SectionSidebarMenu
          items={OVERVIEW_SECTIONS}
          activeId={activeSectionId}
          onSelect={handleSectionChange}
        />
      </div>

      <div className="flex-1 space-y-6">
        <div className="md:hidden mb-4">
          <Tabs
            value={activeSectionId || OVERVIEW_SECTIONS[0].id}
            onChange={handleSectionChange}
            variant="secondary"
            appearance="filled"
            size="md"
            allowWrap
            align="left"
          >
            {OVERVIEW_SECTIONS.map((section) => (
              <Tab key={section.id} value={section.id} label={section.label} />
            ))}
          </Tabs>
        </div>

        <section id="personality" className="!mt-0 !mb-6">
          <Panel
            shadow="flat"
            variant="neutral"
            appearance="outline"
            className="w-full h-full"
          >
            <PanelHeader size="sm" fixed>
              <Text size="h6" weight="medium">
                {/* TODO: check when implementing i18n */}
                Personalidad
              </Text>
            </PanelHeader>

            <div className="p-4 space-y-6">
              <Controller
                name="definition.personality.communicationStyle"
                control={control}
                render={({ field }) => (
                  <div className="space-y-2">
                    {/* TODO: check when implementing i18n */}
                    <WithTooltip
                      tooltip="Define cómo se comunica tu agente IA con tu público"
                      className="!flex-none"
                    >
                      <Text size="small" weight="medium">
                        {/* TODO: check when implementing i18n */}
                        Estilo de comunicación
                      </Text>
                    </WithTooltip>

                    <CommunicationStyleSelector
                      value={
                        field.value as EmployeePersonalityCommunicationStyle
                      }
                      onChange={field.onChange}
                    />
                  </div>
                )}
              />

              <Controller
                name="definition.personality.customTraits"
                control={control}
                render={({ field }) => (
                  // TODO: check when implementing i18n
                  <WithTooltip tooltip="Características específicas sobre la personalidad de tu agente IA">
                    <LabelTextArea
                      // TODO: check when implementing i18n
                      label="Rasgos personalizados"
                      labelPosition="top"
                      inputProps={{
                        value: field.value || '',
                        onChange: field.onChange,
                        // TODO: check when implementing i18n
                        placeholder:
                          'Describe los rasgos de personalidad que tu agente IA debe tener...',
                      }}
                    />
                  </WithTooltip>
                )}
              />
            </div>
          </Panel>
        </section>

        <section id="basic-info" className="!mt-0 !mb-6">
          <Panel
            shadow="flat"
            variant="neutral"
            appearance="outline"
            className="w-full h-full"
          >
            <PanelHeader size="sm" fixed>
              <Text size="h6" weight="medium">
                {/* TODO: check when implementing i18n */}
                Información básica
              </Text>
            </PanelHeader>

            <div className="p-4 space-y-6">
              {/* TODO: check when implementing i18n */}
              <WithTooltip tooltip="Nombre de uso interno para reconocer a tu agente IA">
                <LabelTextInput
                  label="Identificador"
                  labelPosition="top"
                  inputProps={{
                    ...register('metadata.name'),
                    error: errors.metadata?.name?.message,
                    // TODO: check when implementing i18n
                    placeholder: 'Identificador del agente',
                  }}
                />
              </WithTooltip>

              <Controller
                name="definition.personality.name"
                control={control}
                render={({ field }) => (
                  <WithTooltip tooltip="Nombre que tu agente IA usará para identificarse">
                    <LabelTextInput
                      // TODO: check when implementing i18n
                      label="Nombre"
                      labelPosition="top"
                      inputProps={{
                        value: field.value || '',
                        onChange: field.onChange,
                        // TODO: check when implementing i18n
                        placeholder: 'Ej: Juan Pérez',
                        error: errors.definition?.personality?.name?.message,
                      }}
                    />
                  </WithTooltip>
                )}
              />

              {/* TODO: check when implementing i18n */}
              <WithTooltip tooltip="Título o rol del agente IA dentro de la organización">
                <LabelTextInput
                  // TODO: check when implementing i18n
                  label="Título o rol"
                  labelPosition="top"
                  inputProps={{
                    ...register('definition.jobTitle'),
                    error: errors.definition?.jobTitle?.message,
                    // TODO: check when implementing i18n
                    placeholder:
                      'Título o rol de tu agente IA en la organización (opcional)',
                  }}
                />
              </WithTooltip>

              {/* TODO: check when implementing i18n */}
              <WithTooltip tooltip="Breve descripción de la función o propósito de tu agente IA">
                <LabelTextArea
                  label="Descripción"
                  labelPosition="top"
                  inputProps={{
                    ...register('metadata.description'),
                    error: errors.metadata?.description?.message,
                    // TODO: check when implementing i18n
                    placeholder:
                      'Describe la función o propósito del agente IA (opcional)',
                    className: 'min-h-[8rem]',
                  }}
                />
              </WithTooltip>
            </div>
          </Panel>
        </section>

        <section id="behavior" className="!mt-0 !mb-6">
          <Panel shadow="flat" variant="neutral" appearance="outline">
            <PanelHeader size="sm" fixed>
              <Text size="h6" weight="medium">
                {/* TODO: check when implementing i18n */}
                Comportamiento
              </Text>
            </PanelHeader>
            <div className="p-4 space-y-6">
              {/* TODO: check when implementing i18n */}
              <WithTooltip tooltip="Define el propósito principal del agente IA y qué esperas que logre. Intenta ser lo más específico posible para que no queden ambigüedades sobre lo que debe sus funciones.">
                <LabelTextArea
                  label="Objetivo"
                  labelPosition="top"
                  inputProps={{
                    value: objective,
                    onChange: handleObjectiveChange,
                    error: errors.definition?.objectives?.message,
                    // TODO: check when implementing i18n
                    placeholder:
                      'Define el propósito principal del agente IA y qué esperas que logre',
                  }}
                  labelProps={{
                    className: 'font-bold',
                  }}
                />
              </WithTooltip>

              {/* TODO: check when implementing i18n */}
              <ConstraintsField
                label="Restricciones"
                value={newConstraint}
                constraints={constraints}
                // TODO: check when implementing i18n
                tooltip="Reglas que el agente IA debe respetar"
                setNewConstraint={setNewConstraint}
                addConstraint={addConstraint}
                removeConstraint={removeConstraint}
              />
            </div>
          </Panel>
        </section>

        <section id="knowledge-base" className="!mt-0 !mb-6">
          <Panel variant="neutral" appearance="outline">
            <PanelHeader size="sm" fixed>
              <WithTooltip
                // TODO: check when implementing i18n
                tooltip="Recomendamos escribirlo en Markdown. Incluye solo información estática; evita instrucciones o comportamientos esperados."
                className="!flex-none"
              >
                <Text size="h6" weight="medium">
                  {/* TODO: check when implementing i18n */}
                  Base de conocimiento
                </Text>
              </WithTooltip>
            </PanelHeader>
            <div className="p-4 space-y-4">
              <div className="space-y-4">
                <div className="flex flex-col gap-4 md:flex-row md:gap-6 md:h-[calc(100vh-240px)] md:min-h-0">
                  <div className="w-full md:w-1/2 md:flex md:flex-col md:min-h-0">
                    <TextArea
                      ref={(element) => {
                        knowledgeBaseEditRef.current = element;
                        knowledgeBaseRegisterRef(element);
                      }}
                      variant="primary"
                      size="md"
                      className="flex-1 h-full min-h-[320px] max-h-[calc(100vh-240px)] overflow-auto"
                      resize="none"
                      error={errors.definition?.knowledgeBase?.message}
                      placeholder={
                        // TODO: check when implementing i18n
                        'Texto libre que tu agente IA usará como base de conocimiento (opcional)'
                      }
                      {...knowledgeBaseFieldProps}
                    />
                  </div>

                  <div className="w-full md:w-1/2 md:flex md:flex-col md:min-h-0">
                    <div className="space-y-2 md:flex md:flex-col md:h-full md:min-h-0">
                      <div
                        ref={knowledgeBasePreviewRef}
                        className="h-full min-h-[320px] max-h-[calc(100vh-240px)] overflow-auto rounded-md border border-secondary-200 bg-secondary-100 px-4 pt-2 pb-4 md:flex-1"
                      >
                        {hasKnowledgeBaseContent ? (
                          <Markdown className="text-sm text-primary-800">
                            {knowledgeBase}
                          </Markdown>
                        ) : (
                          <Text size="tiny" color="neutral">
                            {/* TODO: check when implementing i18n */}
                            El contenido formateado aparecerá aquí a medida que
                            edites la base de conocimiento.
                          </Text>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Panel>
        </section>
      </div>
    </div>
  );
};
